import * as PropTypes from 'prop-types';

import {isBiologicsEnabled} from 'antibodies/util/biologicsCapability';
import {ArchiveRecord} from 'archive/ArchiveConstants';
import {HasPoliceablePermissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants, {
  SEARCHABLE_EXCLUSIVE_SSI_TYPES,
  SearchableExclusiveSSIType,
  SequenceSubtype,
} from 'constants/ModelTypeConstants';
import * as SourceConstants from 'constants/SourceConstants';
import {NavStoreSource, Source, SourceSummary} from 'constants/SourceConstants';
import {EntitySummary} from 'constants/UserConstants';
import {
  OligoAwareFolderItemType as OligoAwareFolderItemTypeGraphQL,
  RegistryLabelingStrategy,
  TagSchemaAuthParentOption as AuthParentOption,
  TagSchemaConstraintStatus,
  TagSchemaSequenceSchemaType as SequenceSchemaType,
  TagSchemaItemType,
  TypeSpecificConstraintType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {NameTemplatePart} from 'name-templates/NameTemplateConstants';
import {ClientSchemaField} from 'registry/field/FieldTable';
import * as SchemaFieldConstants from 'schemas/fields/SchemaFieldConstants';
import {BaseBioentityType} from 'schemas/schema-interface/SchemaInterfaceBaseBioentityTypeDropdown';
import {Schema, SchemaInterface, SchemaWithDescription} from 'schemas/SchemaConstants';
import {toOligoAwareFolderItemTypeGraphQL} from 'tag-schema/util/ModelTypeConversions';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

export {
  RegistryLabelingStrategy,
  TagSchemaAuthParentOption as AuthParentOption,
  TagSchemaSequenceSchemaType as SequenceSchemaType,
} from 'graphql-client/__generated/graphqlGlobalTypes';

type SchemaField = SchemaFieldConstants.SchemaField;

export interface RegistryFilterOption {
  id: string;
  name: string;
  folderItemType?: string;
}

const REGISTRY_OPTION_SHAPE = PropTypes.shape({
  id: PropTypes.string,
  name: PropTypes.string,
});

export interface RegistryFilters {
  options: Array<RegistryFilterOption>;
  value?: RegistryFilterOption;
}

const REGISTRY_FILTERS_SHAPE = PropTypes.shape({
  options: PropTypes.arrayOf(REGISTRY_OPTION_SHAPE),
  value: REGISTRY_OPTION_SHAPE,
});

const BATCH_SUPPORTED_TYPES: Array<string> = [
  ModelTypeConstants.BASIC_FOLDER_ITEM,
  ModelTypeConstants.SEQUENCE,
  ModelTypeConstants.PROTEIN,
];

export type RegistrySupportedType =
  | ModelTypeConstants.BASIC_FOLDER_ITEM
  | ModelTypeConstants.SEQUENCE
  | ModelTypeConstants.PROTEIN
  | ModelTypeConstants.MIXTURE
  | ModelTypeConstants.MOLECULE;

const REGISTRY_SUPPORTED_TYPES: Array<RegistrySupportedType> = [
  ModelTypeConstants.BASIC_FOLDER_ITEM,
  ModelTypeConstants.MIXTURE,
  ModelTypeConstants.MOLECULE,
  ModelTypeConstants.PROTEIN,
  ModelTypeConstants.SEQUENCE,
];

export function getRegistrySupportedTypes(): Array<RegistrySupportedType> {
  // Returns REGISTRY_SUPPORTED_TYPES with or without ModelTypeConstants.MOLECULE.
  return BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE
    ? REGISTRY_SUPPORTED_TYPES
    : REGISTRY_SUPPORTED_TYPES.filter((supportedType) => supportedType !== ModelTypeConstants.MOLECULE);
}

export function isRegistrySupportedType(folderItemType: string): folderItemType is RegistrySupportedType {
  return (getRegistrySupportedTypes() as Array<string>).includes(folderItemType);
}

export function isLabelingStrategy(labelingStrategy: string): labelingStrategy is RegistryLabelingStrategy {
  return (Object.values(RegistryLabelingStrategy) as Array<string>).includes(labelingStrategy);
}

export type RegistrySupportedOligoAwareType =
  | SequenceSubtype
  | ModelTypeConstants.ANTIBODY
  | ModelTypeConstants.OLIGO_CONJUGATE
  | ModelTypeConstants.OLIGO_DUPLEX
  | ModelTypeConstants.EQUIPMENT
  | ModelTypeConstants.PROTEIN
  | ModelTypeConstants.BASIC_FOLDER_ITEM
  | ModelTypeConstants.MIXTURE
  | ModelTypeConstants.MOLECULE;

export function getRegistrySupportedOligoAwareTypes(): Array<RegistrySupportedOligoAwareType> {
  const REGISTRY_SUPPORTED_OLIGO_AWARE_TYPES: Array<RegistrySupportedOligoAwareType> = [
    ModelTypeConstants.BASIC_FOLDER_ITEM,
    ModelTypeConstants.PROTEIN,
    ModelTypeConstants.MIXTURE,
    ModelTypeConstants.SEQUENCE,
    ModelTypeConstants.RNA_SEQUENCE,
    ModelTypeConstants.DNA_OLIGO,
    ModelTypeConstants.RNA_OLIGO,
  ];
  // Returns REGISTRY_SUPPORTED_OLIGO_AWARE_TYPES with or without ModelTypeConstants.MOLECULE.
  if (BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE) {
    REGISTRY_SUPPORTED_OLIGO_AWARE_TYPES.push(ModelTypeConstants.MOLECULE);
  }
  if (isBiologicsEnabled()) {
    REGISTRY_SUPPORTED_OLIGO_AWARE_TYPES.push(ModelTypeConstants.ANTIBODY);
  }
  if (BenchlingConfig.MODBIO_ENABLE_COMPOSITE_ENTITIES) {
    REGISTRY_SUPPORTED_OLIGO_AWARE_TYPES.push(
      ModelTypeConstants.OLIGO_CONJUGATE,
      ModelTypeConstants.OLIGO_DUPLEX
    );
  }
  if (BenchlingConfig.ENABLE_EQUIPMENT) {
    REGISTRY_SUPPORTED_OLIGO_AWARE_TYPES.push(ModelTypeConstants.EQUIPMENT);
  }
  return REGISTRY_SUPPORTED_OLIGO_AWARE_TYPES;
}

export function isSearchableSsiType(type: string): boolean {
  // SSI Types which are technically basic folder items but are themselves not filterable
  return SEARCHABLE_EXCLUSIVE_SSI_TYPES.includes(type as SearchableExclusiveSSIType);
}

export function getRegistrySupportedOligoAwareTypesGraphQL(): Array<OligoAwareFolderItemTypeGraphQL> {
  return getSearchableRegistrySupportedOligoAwareTypes().map((type) =>
    toOligoAwareFolderItemTypeGraphQL(type)
  );
}

export function getSearchableRegistrySupportedOligoAwareTypes(): Array<RegistrySupportedOligoAwareType> {
  return getRegistrySupportedOligoAwareTypes().filter((type) => !isSearchableSsiType(type));
}

export type TagSchemaSupportedFolderItemType = RegistrySupportedType | ModelTypeConstants.ENTRY;

export const TAG_SCHEMA_SUPPORTED_FOLDER_ITEM_TYPES: Array<TagSchemaSupportedFolderItemType> = [
  ...REGISTRY_SUPPORTED_TYPES,
  ModelTypeConstants.ENTRY,
];

export function isTagSchemaSupportedFolderItemType(
  folderItemType: string
): folderItemType is TagSchemaSupportedFolderItemType {
  return (getTagSchemaSupportedFolderItemTypes() as Array<string>).includes(folderItemType);
}

// These are models that use tag schemas that are also folder items.
// NOTE: Other non-folder items models use tag schemas, e.g. stage configuration, templates, but are
// mapped to folder items via `TagSchemaUtil schemaTagFolderTypeForFileId`.
export function getTagSchemaSupportedFolderItemTypes(): Array<TagSchemaSupportedFolderItemType> {
  const enabledFolderItemTypes: Array<TagSchemaSupportedFolderItemType> = [...getRegistrySupportedTypes()];
  if (BenchlingConfig.ENABLE_ENTRY_SCHEMAS) {
    enabledFolderItemTypes.push(ModelTypeConstants.ENTRY);
  }
  if (BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE) {
    enabledFolderItemTypes.push(ModelTypeConstants.MOLECULE);
  }
  return enabledFolderItemTypes;
}

/**
 * Intermediate folder item type that respects the DNA/oligo split. Rather than "sequence", you must
 * either specify "full_dna" explicitly or "oligo".
 */
export type OligoAwareFolderItemType =
  | ModelTypeConstants.ANTIBODY
  | ModelTypeConstants.EQUIPMENT
  | ModelTypeConstants.OLIGO_CONJUGATE
  | ModelTypeConstants.OLIGO_DUPLEX
  | ModelTypeConstants.ENTRY
  | ModelTypeConstants.BASIC_FOLDER_ITEM
  | ModelTypeConstants.PROTEIN
  | ModelTypeConstants.MIXTURE
  | ModelTypeConstants.MOLECULE
  | ModelTypeConstants.PROTOCOL
  | SequenceSubtype;

export enum FrontendLabelingStrategy {
  CONVERT_IDS = 'CONVERT_IDS',
}

// TODO(T11602): The front end supports CONVERT_IDS in addition to all the back-end values
export type LabelingStrategy = RegistryLabelingStrategy | FrontendLabelingStrategy;

export const ORDERED_LABELING_STRATEGIES: Array<LabelingStrategy> = [
  RegistryLabelingStrategy.NEW_IDS,
  RegistryLabelingStrategy.IDS_FROM_NAMES,
  RegistryLabelingStrategy.DELETE_NAMES,
  RegistryLabelingStrategy.SET_FROM_NAME_PARTS,
  RegistryLabelingStrategy.KEEP_NAMES,
  FrontendLabelingStrategy.CONVERT_IDS,
  RegistryLabelingStrategy.REPLACE_NAMES_FROM_PARTS,
  RegistryLabelingStrategy.REPLACE_ID_AND_NAME_FROM_PARTS,
];

// Keep in sync with LABELING_STRATEGIES_REQUIRING_NAME in
// benchling/registry/labeling_strategy/labeling_strategy.py
const LABELING_STRATEGIES_REQUIRING_NAME: Array<LabelingStrategy> = [
  RegistryLabelingStrategy.NEW_IDS,
  RegistryLabelingStrategy.IDS_FROM_NAMES,
  RegistryLabelingStrategy.KEEP_NAMES,
  RegistryLabelingStrategy.SET_FROM_NAME_PARTS,
];

const MODAL_LABELING_STRATEGIES: Array<RegistryLabelingStrategy> = [
  RegistryLabelingStrategy.NEW_IDS,
  RegistryLabelingStrategy.IDS_FROM_NAMES,
  RegistryLabelingStrategy.DELETE_NAMES,
  RegistryLabelingStrategy.SET_FROM_NAME_PARTS,
  RegistryLabelingStrategy.REPLACE_NAMES_FROM_PARTS,
  RegistryLabelingStrategy.KEEP_NAMES,
  RegistryLabelingStrategy.REPLACE_ID_AND_NAME_FROM_PARTS,
];

export const SPREADSHEET_LABELING_STRATEGIES: Array<LabelingStrategy> = [
  RegistryLabelingStrategy.NEW_IDS,
  FrontendLabelingStrategy.CONVERT_IDS,
  RegistryLabelingStrategy.IDS_FROM_NAMES,
  RegistryLabelingStrategy.DELETE_NAMES,
  RegistryLabelingStrategy.SET_FROM_NAME_PARTS,
  RegistryLabelingStrategy.REPLACE_NAMES_FROM_PARTS,
  RegistryLabelingStrategy.KEEP_NAMES,
  RegistryLabelingStrategy.REPLACE_ID_AND_NAME_FROM_PARTS,
];

// Keep in sync with LABELING_STRATEGY_TO_TEXT in benchling/registry/labeling_strategy/labeling_strategy.py
const LABELING_STRATEGY_TO_TEXT = {
  [RegistryLabelingStrategy.NEW_IDS]: 'Generate new registry IDs',
  [RegistryLabelingStrategy.IDS_FROM_NAMES]: 'Generate registry IDs based on entity names',
  [RegistryLabelingStrategy.DELETE_NAMES]: 'Generate new registry IDs and replace name with registry ID',
  [RegistryLabelingStrategy.SET_FROM_NAME_PARTS]:
    'Generate new registry IDs, rename according to name template, and keep old name as alias',
  [RegistryLabelingStrategy.REPLACE_NAMES_FROM_PARTS]:
    'Generate new registry IDs, and replace name according to name template',
  [RegistryLabelingStrategy.KEEP_NAMES]: 'Keep existing entity names as registry IDs',
  [FrontendLabelingStrategy.CONVERT_IDS]: 'Use specified registry IDs',
  [RegistryLabelingStrategy.REPLACE_ID_AND_NAME_FROM_PARTS]:
    'Generate registry IDs and names according to name template',
};

const LABELING_STRATEGY_INPUT_LABEL = 'Set what happens to registry ID and name on register';

const LABELING_STRATEGY_CONVERT_NAME_TO_ALIAS_TEXT =
  "This entity's previous name will be added to its aliases after registration";

export interface BioentityAliasInfo {
  alias: string;
  registryCollectionOwner: EntitySummary | null;
}

export enum TagSchemaContainableType {
  NONE = 'NONE',
  ENTITY = 'ENTITY',
  BATCH = 'BATCH',
}

export const TAG_SCHEMA_CONTAINABLE_TYPE = {
  NONE: 'NONE',
  ENTITY: 'ENTITY',
  BATCH: 'BATCH',
};

export interface SchemaSummary {
  id: string;
  name: string;
}

export interface TagSchemaSummary extends SchemaSummary {
  archiveRecord?: ArchiveRecord | null;
  iconId: BenchlingIconName;
  itemType: TagSchemaItemType;
  folderItemType: TagSchemaSupportedFolderItemType;
  sequenceType: SequenceSchemaType | null;
  shouldCreateAsOligo: boolean;
  exclusiveSSITypeOverride?: SearchableExclusiveSSIType | null;
  sqlIdentifier: string;
}

export type MixtureSchema = Omit<TagSchema, 'mixtureSchemaConfig'> & {
  mixtureSchemaConfig: MixtureSchemaConfig;
};

export function isMixtureSchema(schema: Schema): schema is MixtureSchema {
  return (schema as TagSchema).mixtureSchemaConfig != null;
}

export interface MixtureSchemaConfig {
  allowMeasuredIngredients: boolean;
  componentLotStorageEnabled: boolean | null;
  componentLotTextEnabled: boolean | null;
}

export interface TagSchema extends SchemaWithDescription, TagSchemaSummary, HasPoliceablePermissions {
  authParentOption: AuthParentOption;
  batchSchemaId: string | null;
  childEntitySchemaSummaries: Array<TagSchemaSummary>;
  constraint: TagSchemaConstraint | null;
  containableType: TagSchemaContainableType;
  hoistedTagSchemaGraphs: Array<SchemaFieldConstants.SchemaFieldPath>;
  includeRegistryIdInChips: boolean;
  isContainable: boolean;
  labelingStrategies: Array<RegistryLabelingStrategy>;
  // If folderItemType is mixture, this is set; otherwise it is null
  mixtureSchemaConfig: MixtureSchemaConfig | null;
  nameTemplateParts: Array<NameTemplatePart>;
  prefix: string;
  registryId: string;
  resultSchema?: null; // Deprecated and does not exist on any deploys
  // If folderItemType is sequence, this is always set; otherwise, this is always unset.
  shouldCreateAsOligo: boolean;
  shouldOrderNamePartsBySequence: boolean;
  showResidues: boolean;
  useOrganizationCollectionAliasForDisplayLabel: boolean;
  useRandomOrgAlias: boolean;
}

export interface TagSchemaWithInterfaces extends TagSchema {
  interfaces: Array<{id: string}>;
}

export interface SchemaInterfaceExtendingSchema extends Schema {
  baseBioentityType: BaseBioentityType | null;
}

const TAG_SCHEMA_SUMMARY_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  folderItemType: PropTypes.string.isRequired,
  sqlIdentifier: PropTypes.string,
});

const TAG_SCHEMA_SHAPE = PropTypes.shape({
  authParentOption: PropTypes.string,
  batchSchemaId: PropTypes.string,
  childEntitySchemaSummaries: PropTypes.arrayOf(TAG_SCHEMA_SUMMARY_SHAPE).isRequired,
  fields: PropTypes.arrayOf(SchemaFieldConstants.SCHEMA_FIELD_SHAPE),
  folderItemType: PropTypes.string.isRequired,
  iconId: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  isContainable: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  prefix: PropTypes.string.isRequired,
  registryId: PropTypes.string.isRequired,
  showResidues: PropTypes.bool.isRequired,
  shouldCreateAsOligo: PropTypes.bool.isRequired,
  sqlIdentifier: PropTypes.string,
});

export const SCHEMA_SUMMARY_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
});

export type ConstraintSource = Pick<SchemaInterface, 'id' | 'name'>;

export interface TagSchemaConstraint {
  id: number;
  areUniqueResiduesCaseSensitive: boolean;
  uniqueResidues: boolean;
  uniqueCanonicalSmiles: boolean;
  fields: Array<SchemaField | ClientSchemaField>;
  status: TagSchemaConstraintStatus;
  source: ConstraintSource | null;
  typeSpecificConstraintConfig?: {
    constraintType: TypeSpecificConstraintType;
  } | null;
}

export interface ClientTagSchemaConstraint extends Omit<TagSchemaConstraint, 'id' | 'status' | 'source'> {}

export function isTagSchemaConstraint(
  constraint: TagSchemaConstraint | ClientTagSchemaConstraint | null
): constraint is TagSchemaConstraint {
  return constraint != null && (constraint as TagSchemaConstraint).id !== undefined;
}

export enum SavingState {
  COMPLETED = 'COMPLETED',
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
  CREATE_SUCCESS = 'CREATE_SUCCESS',
}

interface SupportedUniqueResiduesConstraint {
  areUniqueResiduesCaseSensitive: boolean;
  folderItemType: string;
  text: string;
}

const SUPPORTED_UNIQUE_RESIDUES_CONSTRAINTS: Array<SupportedUniqueResiduesConstraint> = [
  {
    areUniqueResiduesCaseSensitive: false,
    folderItemType: ModelTypeConstants.SEQUENCE,
    text: 'Bases (ignore case)',
  },
  {
    areUniqueResiduesCaseSensitive: true,
    folderItemType: ModelTypeConstants.PROTEIN,
    text: 'Amino acids (exact match)',
  },
  {
    areUniqueResiduesCaseSensitive: false,
    folderItemType: ModelTypeConstants.PROTEIN,
    text: 'Amino acids (ignore case)',
  },
];

interface ChemicalStructureConstraint {
  folderItemType: ModelTypeConstants.MOLECULE;
  text: string;
}

const UNIQUE_CANONICAL_SMILES_CONSTRAINT: ChemicalStructureConstraint = {
  folderItemType: ModelTypeConstants.MOLECULE,
  text: 'Canonical SMILES',
};

export const TYPE_SPECIFIC_CONSTRAINT_TYPE_TO_DISPLAY_NAME: Partial<
  Record<TypeSpecificConstraintType, string>
> = {
  [TypeSpecificConstraintType.UNIQUE_ANTIBODY_STRUCTURE]: 'Protein structure',
  [TypeSpecificConstraintType.UNIQUE_CONJUGATE_STRUCTURE]: 'Conjugate structure',
  [TypeSpecificConstraintType.UNIQUE_SALT_SOLVATE]: 'Salt/solvent composition',
};

export interface RegistrySummary extends SourceSummary {
  sourceType: typeof SourceConstants.REGISTRY_TYPE;
  disablePersonalCollections: boolean;
  areSettingsAdminOnly: boolean;
  useStrictValidation: boolean;
}

// Sometimes it may be necessary to use Registry & Pick<FullSource, 'permissions'>. Source.to_dict() has a few
// flags, and is also often called with permissions attached (e.g. nav.api.get_sources() calls
// Source.batch_to_dict_with_perms()).
export type Registry = RegistrySummary & Source;

// Returned by fetchSources(), and what lives in NavStore.
export type NavStoreRegistry = RegistrySummary &
  NavStoreSource & {
    tag_schemas?: Array<TagSchema>;
  };

export const RESULT_SUPPORTED_TYPES = [
  ModelTypeConstants.BASIC_FOLDER_ITEM,
  ModelTypeConstants.SEQUENCE,
  ModelTypeConstants.PROTEIN,
];

export const RESIDUES_SUPPORTED_OLIGO_AWARE_TYPES = [
  ModelTypeConstants.PROTEIN,
  ModelTypeConstants.SEQUENCE,
  ModelTypeConstants.RNA_SEQUENCE,
  ModelTypeConstants.DNA_OLIGO,
  ModelTypeConstants.RNA_OLIGO,
];

// Currently, we only support schemaless bulk import of a few file types
export const SCHEMALESS_BULK_IMPORT_SUPPORTED_OLIGO_AWARE_TYPES = [
  ModelTypeConstants.DNA_OLIGO,
  ModelTypeConstants.RNA_OLIGO,
  ModelTypeConstants.SEQUENCE,
  ModelTypeConstants.RNA_SEQUENCE,
  ModelTypeConstants.PROTEIN,
  ModelTypeConstants.MOLECULE,
];

export type SchemalessBulkImportSupportedOligoAwareTypes =
  | SequenceSubtype
  | ModelTypeConstants.PROTEIN
  | ModelTypeConstants.MOLECULE;

export const SAVING_STATES = {
  COMPLETED: 'COMPLETED',
  PENDING: 'PENDING',
  SUCCESS: 'SUCCESS',
  FAILURE: 'FAILURE',
  CREATE_SUCCESS: 'CREATE_SUCCESS',
};

export const REGISTRY_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
});

export const DEFAULT_MY_COLLECTION = 'DEFAULT_MY_COLLECTION';
export const DEFAULT_ORG_COLLECTION = 'DEFAULT_ORG_COLLECTION';

// Keep in sync with REGISTRY_SUPPORTED_TYPES
const VALIDATION_RULES_SUPPORTED_TYPES: Array<TagSchemaItemType> = [
  TagSchemaItemType.BASIC_FOLDER_ITEM,
  TagSchemaItemType.AA_SEQUENCE,
  TagSchemaItemType.ANTIBODY,
  TagSchemaItemType.DNA_OLIGO,
  TagSchemaItemType.DNA_SEQUENCE,
  TagSchemaItemType.EQUIPMENT,
  TagSchemaItemType.RNA_OLIGO,
  TagSchemaItemType.RNA_SEQUENCE,
  TagSchemaItemType.MIXTURE,
  TagSchemaItemType.MOLECULE,
  TagSchemaItemType.MIXED,
];

export function isValidationRulesSupportedType(type: TagSchemaItemType | null): boolean {
  if (type == null) {
    return false;
  }
  return VALIDATION_RULES_SUPPORTED_TYPES.includes(type);
}

// Types that can changed to another type after schema creation, if no entities exist yet.
const EDITABLE_TYPES: Array<TagSchemaItemType> = [
  TagSchemaItemType.AA_SEQUENCE,
  TagSchemaItemType.BASIC_FOLDER_ITEM,
  TagSchemaItemType.DNA_OLIGO,
  TagSchemaItemType.DNA_SEQUENCE,
  TagSchemaItemType.ENTRY,
  TagSchemaItemType.EQUIPMENT,
  TagSchemaItemType.MIXED,
  TagSchemaItemType.MIXTURE,
  TagSchemaItemType.MOLECULE,
  TagSchemaItemType.RNA_OLIGO,
  TagSchemaItemType.RNA_SEQUENCE,
];

// Returns true if the type can be changed to another type after schema creation, if no entities exist yet.
export function isEditableType(type: TagSchemaItemType | null): boolean {
  if (type == null) {
    return true;
  }
  return EDITABLE_TYPES.includes(type);
}

export {
  BATCH_SUPPORTED_TYPES,
  SUPPORTED_UNIQUE_RESIDUES_CONSTRAINTS,
  LABELING_STRATEGIES_REQUIRING_NAME,
  MODAL_LABELING_STRATEGIES,
  LABELING_STRATEGY_CONVERT_NAME_TO_ALIAS_TEXT,
  LABELING_STRATEGY_INPUT_LABEL,
  LABELING_STRATEGY_TO_TEXT,
  REGISTRY_OPTION_SHAPE,
  REGISTRY_FILTERS_SHAPE,
  TAG_SCHEMA_SHAPE,
  UNIQUE_CANONICAL_SMILES_CONSTRAINT,
  REGISTRY_SUPPORTED_TYPES,
  VALIDATION_RULES_SUPPORTED_TYPES,
};

export const RegistryPreferences = {
  ENABLE_REGISTRY_ACTIVATION: 'ENABLE_REGISTRY_ACTIVATION',
  DENORMALIZE_ACADEMIC_HAS_REGISTRY: 'DENORMALIZE_ACADEMIC_HAS_REGISTRY',
  REGISTRY_ACTIVATION_COMPLETE: 'REGISTRY_ACTIVATION_COMPLETE',
  DEFAULT_COLLECTION: 'default_collection',
  ENABLE_ENTRY_SCHEMA_REMOVE_FIELDS: 'ENABLE_ENTRY_SCHEMA_REMOVE_FIELDS',
  DEFAULT_REGISTRY: 'RegistrySelector.defaultRegistryId',
  LABELING_STRATEGY_NAMESPACE: 'NAME_OPTION_PREFERENCE_PREFIX-', // Labeling strategies used to be named name options, so keep the old preference for back-compatibility.
  ENABLE_DROPDOWN_DELETE: 'ENABLE_DROPDOWN_DELETE',
};

export interface UnregisteredBioentity {
  id: string;
  name: string;
  bioentityAliasInfos: Array<BioentityAliasInfo>;
  displayLabel?: string;
}
